/** src/templates/Hotel.js */
// Import Core
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
} from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
// Import Libraries
import { Breadcrumb } from "gatsby-plugin-breadcrumb";
import { PortableText } from "@portabletext/react";
// Import Components
import SEO from "../components/SEO";
import HotelHero from "../components/blocks/Hero/HotelHero";
import AnchorMenu from "../components/blocks/AnchorMenu";
import GoogleMap from "../components/blocks/Map";
import CardsCarousel from "../components/blocks/Cards/CardsCarousel";
import AdditionalCards from "../components/blocks/Cards/AdditionalCards";
import HotelCTAGroup from "../components/ui/HotelCTAGroup";
import Modal from "../components/ui/Modal";
import ReadMoreReadLess from "../components/ui/ReadMoreReadLess";
import { Button } from "../components/ui/Button";
import Pagination from "../components/ui/Pagination";
import PortableTextComponent from "../components/ui/PortableTextComponent";
// Import Icons (including facilities options that may / may not all be used)
import {
  FaWheelchair,
  FaUtensils,
  FaWifi,
  FaBuilding,
  FaUmbrellaBeach,
  FaParking,
  FaConciergeBell,
  FaClock,
  FaLock,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaBed,
  FaMusic,
  FaBaby,
  FaDog,
  FaCreditCard,
  FaDollarSign,
  FaNewspaper,
  FaCheck,
  FaAngleRight,
  FaInfoCircle,
} from "react-icons/fa";
// Import Utils
import useModal from "../utils/useModal";
import transformToUpperCase from "../utils/transformToUpperCase";
import checkSpecialOffersValid from "../utils/checkSpecialOffersValid";
import isHotelInternational from "../utils/isHotelInternational";
import handleTrackingLinkClicks from "../utils/tracking/handleTrackingLinkClick";
import { renderFontAwesomeIcon } from "../utils/renderFontAwesomeIcon";

/* Styled Components */
const HotelStyles = styled.div`
  background: var(--neutral-50);
  .hotel_main {
    position: relative;
    display: flex;
    columns: 2;
    justify-content: space-between;
    padding: 20px 120px 64px;
    gap: 40px;
    @media (max-width: 992px) {
      padding: 20px 64px 64px;
      gap: 32px;
    }
    @media (max-width: 768px) {
      padding: 20px 0 40px;
      flex-direction: column-reverse;
    }
    .hotel_content {
      display: flex;
      flex-direction: column;
      width: fit-content;
      @media (max-width: 768px) {
        padding: 0 36px;
        width: unset;
      }
      h2 {
        color: var(--neutral-900);
      }
      h4,
      p {
        color: var(--neutral-800);
      }
      p {
        margin-bottom: 16px;
        &:last-child {
          margin-bottom: 0;
        }
      }
      .hotel_review {
        padding: 40px 36px;
        gap: 10px;
        background: var(--white);
        box-shadow: 0px 6px 20px -4px rgba(0, 0, 0, 0.1);
        border-radius: 5px;
        /* margin-bottom: 64px; */
        h2 {
          margin-bottom: 8px;
        }
        .tooltip {
          margin-bottom: 32px;
          p {
            margin-bottom: 0;
          }
        }
      }
      .hotel_special-offers {
        display: flex;
        flex-direction: column;
        row-gap: 32px;
        margin-top: 64px;
        > div {
          gap: 32px;
          display: flex;
          flex-direction: column;
          @media (max-width: 768px) {
            gap: 24px;
          }
        }
        .promotedHotelAnnouncements {
          font-size: 1.1rem;
          background-color: #f3e5ee;
          padding: 20px;
        }
      }
      .hotel_media {
        h2 {
          margin-bottom: 40px;
        }
        .media-list {
          display: flex;
          flex-direction: column;
          gap: 40px;
          li {
            display: flex;
            gap: 24px;
            align-items: center;
            .icon {
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              width: 70px;
              min-width: 70px;
              height: 70px;
              background: rgba(0, 0, 0, 0.03);
              border-radius: 50%;
              svg {
                path {
                  color: #848484;
                }
              }
            }
            div {
              display: flex;
              flex-direction: column;
              gap: 8px;
              .article-name {
                display: flex;
                margin-bottom: 0;
                @media (max-width: 576px) {
                  flex-direction: column;
                  gap: 8px;
                }
              }
              span {
                margin-left: 24px;
                @media (max-width: 576px) {
                  margin-left: unset;
                }
              }
            }
          }
        }
      }
      hr {
        margin: 64px 0;
        border-top: 1px solid var(--neutral-300);
        @media (max-width: 768px) {
          margin: 40px 0;
        }
      }
    }
    .anchor-link {
      scroll-margin-top: 100px;
    }
  }
  .hotel_attractions {
    background: var(--white);
  }
`;

const HotelListStyles = styled.section`
  // text-transform: capitalize;
  h2 {
    margin-bottom: 40px;
  }
  .hotel_details {
    width: 100%;
    display: flex;
    @media (max-width: 1200px) {
      flex-direction: column;
    }
    ul {
      list-style: none;
      gap: 8px;
      display: flex;
      flex-direction: column;
      li {
        display: flex;
        gap: 10px;
        svg {
          min-width: 30px;
          padding-top: 6px;
          color: var(--neutral-800);
        }
        div {
          display: flex;
          flex-direction: column;
          gap: 4px;
          ul {
            p {
              display: flex;
              flex-direction: column;
            }
          }
        }
        li {
          svg {
            padding-top: 3px;
          }
        }
      }
    }
    .left {
      width: calc(50% - 20px);
      margin-right: 20px;
      @media (max-width: 1200px) {
        width: 100%;
        margin-right: unset;
        margin-bottom: 8px;
      }
    }
    .right {
      width: calc(50% - 20px);
      margin-left: 20px;
      @media (max-width: 1200px) {
        width: 100%;
        margin-left: unset;
      }
    }
  }
`;

const AffiliateProductStyles = styled.section`
  h2 {
    margin-bottom: 40px;
  }

  a {
    text-decoration: none;
  }
  .products {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 25px;
    padding: 20px;
    background-color: #f3e5ee;
    max-width: 600px;

    .text-copy {
      h3 {
        margin-bottom: 10px;
      }
    }

    @media (min-width: 1200px) {
      flex-direction: row;
      padding: 25px;
      .image {
        width: 250px;
      }
      .text-copy {
        max-width: 400px;
      }
    }
  }
`;

const HotelStickyCtaStyles = styled.div`
  width: 376px;
  height: fit-content;
  position: sticky;
  top: 200px;
  background: var(--white);
  color: var(--neutral-800);
  display: flex;
  flex-direction: column;
  padding: 40px 36px;
  gap: 32px;
  box-shadow: 0px 6px 20px -4px rgba(0, 0, 0, 0.1);
  border-radius: 4px;

  @media (max-width: 768px) {
    width: calc(100% - 36px - 36px);
    position: unset;
  }

  h5 {
    color: var(--neutral-900);
  }
  .cta-subheading {
    /* Offset gap of 32 so it is 12px */
    margin-bottom: calc(12px - 32px);
  }

  .hotel_cta-group {
    width: 100%;
    flex-direction: column;
    row-gap: 16px;
  }
`;

/* One Off Components / Functions */
const Address = ({ address }) => {
  let addressArr = [
    transformToUpperCase(address.street?.toLowerCase(), ["-"]),
    transformToUpperCase(address.village?.toLowerCase(), ["-"]),
    transformToUpperCase(address.town?.toLowerCase(), ["-"]),
    transformToUpperCase(address.county?.toLowerCase(), ["-"]),
    address.postCode?.toUpperCase(),
    address?.country,
  ];
  // remove empty strings from array
  addressArr = addressArr.filter((place) => place);
  let addressString = addressArr.map((element, index) => {
    if (element !== "") {
      return index === addressArr.length - 1
        ? `${element}`
        : `${element.replaceAll(",", "")}, `;
    }
    return index;
  });
  return <>{addressString}</>;
};
// Other Reviews section in hotel information
const FeaturedMedia = ({ media }) => {
  // [{ name: `${hotel.hotelInformation.featuredMedia}` }]
  // const featuredMediaArr =
  return media.map((article) => {
    return (
      <a href={article.externalLink} key={article._key}>
        {article.name}
      </a>
    );
  });
};

// Hotel Details Section - Calls List Component
const HotelDetailsArr = ({ hotel }) => {
  const title = "Hotel Details";
  let arr = [
    {
      icon: <FaMapMarkerAlt />,
      heading: "Address",
      description: [{ name: <Address address={hotel.addressGroup} /> }],
    },

    // Check if displayEmail is checked
    hotel.contactGroup.displayEmail
      ? hotel.contactGroup.contactEmailAddress
        ? {
            icon: <FaEnvelope />,
            heading: "Email",
            description: [
              { name: `${hotel.contactGroup.contactEmailAddress}` },
            ],
          }
        : null
      : hotel.contactGroup.telephone
      ? {
          icon: <FaPhoneAlt />,
          heading: "Telephone",
          description: [{ name: `${hotel.contactGroup.telephone}` }],
        }
      : null,
    hotel.hotelInformation.bedrooms
      ? {
          icon: <FaBed />,
          heading: "Bedroom",
          description: [{ name: `${hotel.hotelInformation.bedrooms}` }],
        }
      : null,
    hotel.hotelInformation.open
      ? {
          icon: <FaClock />,
          heading: "Openings",
          description: [{ name: `${hotel.hotelInformation.open}` }],
        }
      : null,
    hotel.hotelInformation.backgroundMusic
      ? {
          icon: <FaMusic />,
          heading: "Background Music",
          description: [{ name: `${hotel.hotelInformation.backgroundMusic}` }],
        }
      : null,
    hotel.hotelInformation.children
      ? {
          icon: <FaBaby />,
          heading: "Children",
          description: [{ name: `${hotel.hotelInformation.children}` }],
        }
      : null,
    hotel.hotelInformation.dogs
      ? {
          icon: <FaDog />,
          heading: "Dogs",
          description: [{ name: `${hotel.hotelInformation.dogs}` }],
        }
      : null,
    /*     hotel.hotelInformation.creditCards
      ? {
          icon: <FaCreditCard />,
          heading: "Credit Cards",
          description: [{ name: `${hotel.hotelInformation.creditCards}` }],
        }
      : null, */
    hotel.hotelInformation.prices
      ? {
          icon: <FaDollarSign />,
          heading: "Prices",
          description: [{ name: `${hotel.hotelInformation.prices}` }],
        }
      : null,
    // Featured Media
    hotel.hotelInformation.featuredMedia &&
    hotel.hotelInformation.featuredMedia.length > 0
      ? {
          icon: <FaNewspaper />,
          heading: "Other Reviews",
          description: [
            {
              name: (
                <FeaturedMedia media={hotel.hotelInformation.featuredMedia} />
              ),
            },
          ],
        }
      : null,
  ];
  arr = arr.filter((item) => item);

  // Split array into halves
  const half = Math.ceil(arr.length / 2);
  const leftArr = arr.slice(0, half);
  const rightArr = arr.slice(half);

  const list = { title: title, leftCol: leftArr, rightCol: rightArr };
  return <HotelList listItems={list} />;
};

// Get all Facilities - group by type and then send to HotelList component
const HotelFacilitiesArr = ({ hotel }) => {
  const facilities = hotel.hotelInformation.facilities;

  // Filter to get type of Facility
  const filterType = (type) =>
    facilities.filter((facil) => facil.facilityType === type);
  // Loop through each facilty type and get list of facilities in that category
  const itemsArr = (item) =>
    item.map((el) => {
      // If else statement,e.g., if icon = FaBeer show FaBeer Icon etc
      const Icon = renderFontAwesomeIcon(el.icon.name);

      // console.log('el', el.icon.name)
      // // Convert Icon from Sanity field to a React Icon Component
      // // const Icon = Icons[toPascalCase(el.icon.name)]
      // const Icon = Icons[el.icon.name];
      // // const Icon = el.icon.name;
      return { itemIcon: Icon, name: el.name };
    });

  const roomAmenities = filterType("roomAmenities");
  const roomAmenitiesDescription = {
    icon: <FaBed />,
    heading: "Room Amenities",
    description: itemsArr(roomAmenities),
  };
  const accessibility = filterType("accessibility");
  const accessibilityDescription = {
    icon: <FaWheelchair />,
    heading: "Accessibility",
    description: itemsArr(accessibility),
  };
  const parking = filterType("parking");
  const parkingDescription = {
    icon: <FaParking />,
    heading: "Parking",
    description: itemsArr(parking),
  };

  const foodDrink = filterType("foodDrink");
  const foodDrinkDescription = {
    icon: <FaUtensils />,
    heading: "Food & Drink",
    description: itemsArr(foodDrink),
  };

  const service = filterType("service");
  const serviceDescription = {
    icon: <FaConciergeBell />,
    heading: "Service",
    description: itemsArr(service),
  };

  const internetAccess = filterType("internetAccess");
  const internetAccessDescription = {
    icon: <FaWifi />,
    heading: "Internet Access",
    description: itemsArr(internetAccess),
  };

  const safetySecurity = filterType("SafetySecurity");
  const safetySecurityDescription = {
    icon: <FaLock />,
    heading: "Safety & Security",
    description: itemsArr(safetySecurity),
  };

  const functionRooms = filterType("functionRooms");
  const functionRoomsDescription = {
    icon: <FaBuilding />,
    heading: "Function Rooms",
    description: itemsArr(functionRooms),
  };

  const activities = filterType("activities");
  const activitiesDescription = {
    icon: <FaUmbrellaBeach />,
    heading: "Activities",
    description: itemsArr(activities),
  };

  const misc = filterType("misc");
  const miscDescription = {
    icon: <FaCheck />,
    heading: "Misc",
    description: itemsArr(misc),
  };

  const title = "Facilities";
  let arr = [
    roomAmenitiesDescription,
    accessibilityDescription,
    parkingDescription,
    foodDrinkDescription,
    serviceDescription,
    internetAccessDescription,
    safetySecurityDescription,
    functionRoomsDescription,
    activitiesDescription,
    miscDescription,
  ];
  // Filter out any that have no description (e.g., facilities listed)
  arr = arr.filter((facility) => facility.description.length);

  // Split array into halves
  const half = Math.ceil(arr.length / 2);
  const leftArr = arr.slice(0, half);
  const rightArr = arr.slice(half);

  const list = { title: title, leftCol: leftArr, rightCol: rightArr };
  return <HotelList listItems={list} />;
};

/* Components */
const HotelList = ({ listItems }) => {
  const list = listItems;

  const ListColumn = ({ column }) => {
    return (
      <li key={column.heading}>
        {column.icon && column.icon}
        <div>
          <h4>{column.heading}</h4>
          {column.description &&
            column.description.map((el) => {
              let isNumber = /^\d+$/.test(el.name);
              return (
                <ul key={el.name}>
                  <li key={el.name}>
                    {el.itemIcon && el.itemIcon}
                    {isNumber ? (
                      <a href={`tel:${el.name}`}>{el.name}</a>
                    ) : (
                      <p className="hotel-details-p">{el.name}</p>
                    )}
                  </li>
                </ul>
              );
            })}
        </div>
      </li>
    );
  };

  return (
    <>
      <h2>{list.title}</h2>
      <div className="hotel_details">
        <ul className="left">
          {list.leftCol.map((column, index) => {
            return (
              <ListColumn column={column} key={`l-${list.title}-${index}`} />
            );
          })}
        </ul>
        <ReadMoreReadLess
          className="right"
          content={list.rightCol.map((column, index) => {
            return (
              <ListColumn column={column} key={`r-${list.title}-${index}`} />
            );
          })}
        />
      </div>
    </>
  );
};

const HotelCTA = ({ hotel, hotelCtaText }) => {
  return (
    <HotelStickyCtaStyles>
      <h5 className="cta-subheading">Book direct with</h5>
      <h2>{hotel.name}</h2>
      {hotelCtaText && <p>{hotelCtaText}</p>}
      <HotelCTAGroup
        website={hotel.contactGroup.websiteUrl}
        bookingUrl={hotel.contactGroup.bookingUrl}
        bookNowUrl={hotel.contactGroup.bookNowUrl}
        displayEmail={hotel.contactGroup.displayEmail}
        email={hotel.contactGroup.contactEmailAddress}
        telephone={hotel.contactGroup.telephone}
        hasPaid={hotel.displayOptions.paid}
        hotelName={hotel.name}
        slug={hotel.slug.current}
      />
    </HotelStickyCtaStyles>
  );
};

const HotelSpecialOffers = ({
  hotel,
  specialOffers,
  specialOfferEnabled,
  setPageLoaded,
}) => {
  // We use this to indicate that the page has loaded when this component renders - then we can conditionally call Anchor Bar component
  useEffect(() => {
    setPageLoaded();
  }, [setPageLoaded]);

  // enabled
  // startDate
  // endDate
  // if offers are enabled we can return section - we are only pulling in enabled offers in graphql query but check that they are in date
  if (specialOfferEnabled === true) {
    return (
      <section
        className="anchor-link hotel_special-offers"
        id="anchor_special-offers"
      >
        {specialOffers.map((offer, index) => {
          return (
            offer.enabled && (
              <div key={index}>
                {offer?.promoted && offer?.promotedTagTextOne?.length > 0 ? (
                  <h2>{offer.promotedTagTextOne}</h2>
                ) : (
                  ""
                )}
                {offer?.promoted &&
                offer?.promotedHotelAnnouncements?.length > 0 ? (
                  <p className="promotedHotelAnnouncements">
                    {offer.promotedHotelAnnouncements}
                  </p>
                ) : (
                  ""
                )}
                <h2>{offer.title}</h2>
                {offer._rawOfferDescriptionRichText !== null ? (
                  <PortableText
                    value={offer._rawOfferDescriptionRichText}
                    components={PortableTextComponent}
                  />
                ) : (
                  <p>{offer.offerDescription}</p>
                )}

                {hotel.contactGroup.bookNowUrl && (
                  <a
                    href={
                      offer.specialOfferCustomLink !== null
                        ? offer.specialOfferCustomLink
                        : hotel.contactGroup.bookNowUrl.includes("https")
                        ? hotel.contactGroup.bookNowUrl
                        : hotel.contactGroup.bookNowUrl.replace("http", "https")
                    }
                    target="_blank"
                    className="special_offer_book"
                    onClick={() =>
                      handleTrackingLinkClicks(
                        "Direct Booking (Special Offer)",
                        hotel.name,
                        hotel.slug.current
                      )
                    }
                  >
                    <Button className="large">Book Now</Button>
                  </a>
                )}
              </div>
            )
          );
        })}
      </section>
    );
  } else {
    return null;
  }
};

/* Render Page */
const HotelReview = ({ data, location }) => {
  const hotel = data.hotels;
  const { siteSettings } = data;
  const pillarPages = data.allPillarPagesSearchInfo.nodes;
  const allHotels = data.allHotelSearchInfo.nodes;
  const specialOffers = data.specialOffers.nodes;
  const inTheMedia = data.inTheMedia.nodes;
  let editorsChoiceBadges = [];

  data.badges.nodes.forEach((elem) => {
    elem.editorsChoiceSection.editorsChoiceSection.forEach((el1) => {
      el1.editorsSelectedHotels.forEach((el2) => {
        if (el2._id === hotel._id) {
          if (editorsChoiceBadges.find((el) => el1.title === el.title)) {
            return;
          } else {
            editorsChoiceBadges.push({ ...el1, slug: elem.slug });
          }
        }
      });
    });
  });

  const [isModalOpen, toggle] = useModal();
  const [anchorLinks, setAnchorLinks] = useState([]);
  const [intlHotel, setIntlHotel] = useState();
  const [specialOfferEnabled, setSpecialOfferEnabled] = useState(false);
  // Check if Special Offers loaded
  const [hasSpecialOffersRendered, setHasSpecialOffersRendered] =
    useState(false);

  // const [scrollToSection, setScrollToSection] =
  // useState('___gatsby');

  const setPageLoaded = useCallback(() => {
    setHasSpecialOffersRendered(true);
  }, []);

  // Run this before page renders to check if special offers are enabled and within timeframe
  useLayoutEffect(() => {
    // Check array of offers to see if any offers are enabled
    checkSpecialOffersValid({ specialOffers, setSpecialOfferEnabled });
  }, [specialOffers]);

  // Once special offers has rendered and if there was a scroll id (e.g the link from special offers archive - scroll to that section)
  // const useScrollTo = id => {
  // useLayoutEffect(() => {
  // 		if(hasSpecialOffersRendered && location.state) {
  // 			const el = document.getElementById(location.state.targetFragment)
  // 			const top = (window.scrollY + el.getBoundingClientRect().top) - 100
  // 			window.scrollTo({ top, behavior: "smooth" })
  // 		}
  // }, [hasSpecialOffersRendered, location])
  // }
  // If hotel is clicked from special offers archive page - scroll to section
  // useScrollTo(scrollToSection)

  // check if hotel is international
  // const intlHotel = isHotelInternational(hotel.addressGroup.country)

  // Changing state will always cause a re-render. By default, useEffect always runs after render has run. This means if you don't include a dependency array when using useEffect to fetch data, and use useState to display it, you will always trigger another render after useEffect runs.
  // Need to use useLayoutEffect as render depends on this
  useLayoutEffect(() => {
    // console.log('use effect firing');
    window.dataLayer.push({
      event: "hotel_page_view",
      hotel_name: hotel.name,
      hotel_slug: hotel.slug.current,
      hotel_id: hotel._id,
    });

    // Check if hotel is international and set as a hook
    setIntlHotel(isHotelInternational(hotel?.addressGroup?.country));
  }, [hotel]);

  // -> BREADCRUMBS
  // If no pillar page for the hotel's country exist then we will not include country in the breadcrumb
  let crumbParentPage = pillarPages
    .filter(
      (page) =>
        page?.slug?.current?.toLowerCase() ===
        `hotels-in-${hotel.addressGroup?.country?.toLowerCase()}`
    )
    .map((page) => page.slug.current)
    .toString();

  const hotelCrumbs = crumbParentPage
    ? [
        { pathname: "/", crumbLabel: "Home" },
        {
          pathname: `/hotels-in-${hotel.addressGroup?.country?.toLowerCase()}`,
          crumbLabel: hotel.addressGroup?.country,
        },
        { pathname: `/review/${hotel?.slug?.current}`, crumbLabel: hotel.name },
      ]
    : [
        { pathname: "/", crumbLabel: "Home" },
        { pathname: `/review/${hotel.slug.current}`, crumbLabel: hotel.name },
      ];

  // -> Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(4);

  // Use Ref to scroll to the top of cards when pagination is used
  const ref = useRef(null);

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = inTheMedia.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const nPages = Math.ceil(inTheMedia.length / recordsPerPage);

  // return !documentReady ?
  //   (<Loader />)
  // :
  return (
    <HotelStyles>
      <Breadcrumb crumbs={hotelCrumbs} crumbSeparator={<FaAngleRight />} />
      <HotelHero
        data={hotel}
        intlHotel={intlHotel}
        specialOffers={specialOffers}
        specialOfferEnabled={specialOfferEnabled}
        shortlistedHotelInfo={siteSettings.shortlistedHotelInfo}
        inGuideNotPaidContent={siteSettings.inGuideNotPaidContent}
        editorsChoiceBadges={editorsChoiceBadges}
        placeholderImage={data.placeholderImages.generalPlaceholderImage}
      />

      <div className="main-content">
        {/* If hotel has paid or is international then show review content */}
        {(hotel.displayOptions.paid || intlHotel) && (
          <>
            {/* Render this component after special Offers have been rendered */}
            {hasSpecialOffersRendered && (
              <AnchorMenu
                anchorLinks={anchorLinks}
                setAnchorLinks={setAnchorLinks}
                location={location}
              />
            )}
            <div className="hotel_main">
              <div className="hotel_content">
                <section
                  className="hotel_review anchor-link"
                  id="anchor_good-hotel-guide-review"
                >
                  <h2>Good Hotel Guide Review</h2>
                  <div className="tooltip">
                    <p className="x-small">
                      The hotel experts at the Good Hotel Guide
                    </p>
                    <FaInfoCircle className="link" onClick={toggle} />
                    <Modal
                      id="experts"
                      isModalOpen={isModalOpen}
                      hide={toggle}
                      content={<p>{siteSettings.aboutGoodHotelGuideExperts}</p>}
                    />
                  </div>

                  {hotel.reviewTextAndMedia.reviewContent && (
                    <div className="hero_content">
                      <PortableText
                        value={hotel.reviewTextAndMedia.reviewContent}
                        components={PortableTextComponent}
                      />
                    </div>
                  )}
                </section>

                <HotelSpecialOffers
                  hotel={hotel}
                  specialOffers={specialOffers}
                  specialOfferEnabled={specialOfferEnabled}
                  setSpecialOfferEnabled={setSpecialOfferEnabled}
                  setPageLoaded={setPageLoaded}
                />

                <hr />

                <HotelListStyles
                  className="anchor-link"
                  id="anchor_hotel-details"
                >
                  <HotelDetailsArr hotel={hotel} />
                </HotelListStyles>

                <hr />

                {hotel?.productAffiliate &&
                  hotel?.productAffiliate.length > 0 && (
                    <AffiliateProductStyles>
                      <h2>Quality products offered at this hotel</h2>
                      <div className="products">
                        {hotel.productAffiliate.map((product) => {
                          return (
                            <a
                              href={product.link}
                              target="_blank"
                              className="affiliate-link"
                            >
                              <div className="content">
                                <GatsbyImage
                                  class="image"
                                  image={getImage(product.image.asset)}
                                  alt={product.name}
                                />
                                <div className="text-copy">
                                  <h3>{product.name}</h3>
                                  <p>{product.description}</p>
                                </div>
                              </div>
                            </a>
                          );
                        })}
                      </div>

                      <hr />
                    </AffiliateProductStyles>
                  )}

                {/* ORIGINAL FACILITIES LIST AS A STRING */}
                {hotel.fromOldSiteToTransfer &&
                  hotel.fromOldSiteToTransfer.toTransferFacilities && (
                    <HotelListStyles
                      className="anchor-link"
                      id="anchor_facilities"
                    >
                      <h2>Facilities</h2>
                      <p>{hotel.fromOldSiteToTransfer.toTransferFacilities}</p>
                    </HotelListStyles>
                  )}
                {hotel.fromOldSiteToTransfer &&
                  hotel.fromOldSiteToTransfer.toTransferFacilities && <hr />}

                {/* FACILITIES WILL EVENTUALLY USE THIS LIST DISPLAY */}
                {hotel.hotelInformation.facilities.length > 0 && (
                  <HotelListStyles
                    className="anchor-link"
                    id="anchor_facilities"
                  >
                    <HotelFacilitiesArr hotel={hotel} />
                  </HotelListStyles>
                )}
                {hotel.hotelInformation.facilities.length > 0 && <hr />}

                {hotel.location && (
                  <section className="anchor-link" id="anchor_map">
                    <GoogleMap
                      lat={hotel.location.lat}
                      lng={hotel.location.lng}
                    />
                  </section>
                )}
                {hotel.location && <hr />}

                {/* <p className="anchor-link" id="anchor_guest-comments">GUEST COMMENTS</p>

									<hr /> */}

                {inTheMedia.length > 0 && (
                  <section
                    className="hotel_media anchor-link"
                    id="anchor_media"
                  >
                    <h2>IN THE MEDIA</h2>
                    <ul ref={ref} className="media-list">
                      {currentRecords.map((article) => {
                        return (
                          <li key={article.id}>
                            <span className="icon">
                              <FaBuilding />
                            </span>
                            <div>
                              <p className="article-name">
                                {article.publicationName}
                                {/* <span>
																		{getShortDate(article._createdAt)}
																	</span> */}
                              </p>
                              <a
                                href={article.externalLink}
                                target="_blank"
                                className="in_the_media_outbound_link"
                              >
                                {article.name}
                              </a>
                            </div>
                          </li>
                        );
                      })}
                    </ul>

                    {nPages > 1 && (
                      <Pagination
                        nPages={nPages}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        // smoothScrollOnClick={smoothScrollOnClick}
                      />
                    )}
                  </section>
                )}
              </div>
              <HotelCTA
                hotel={hotel}
                hotelCtaText={siteSettings.hotelCtaText}
              />
            </div>
          </>
        )}
      </div>

      {/* IF NOT A PAID HOTEL THEN SHOW SIMILAR HOTELS BLOCK */}
      {!hotel.displayOptions.paid && (
        <AdditionalCards
          cardsType="nearby"
          resultsType={hotel}
          allPages={allHotels}
          placeholderImage={data.placeholderImages.generalPlaceholderImage}
        />
      )}

      {/* IF NOT A PAID HOTEL DON`T SHOW ATTRACTIONS */}
      {/* {hotel.attractions.length > 0 && hotel.displayOptions.paid && ( */}
      {hotel.attractions.length > 0 && (
        <section
          className="hotel_attractions anchor-link"
          id="anchor_attractions"
        >
          <CardsCarousel
            sectionTitle={{
              title: "Nearby Attractions",
            }}
            data={hotel.attractions}
            // placeholderImage={data.placeholderImages.attractionPlaceholderImage}
          />
        </section>
      )}
    </HotelStyles>
  );
};
export default HotelReview;

/** Attach SEO */
export const Head = ({ data }) => {
  const hotel = data.hotels;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Hotel",
    name: hotel?.name,
    url: `https://www.goodhotelguide.com/${
      hotel?.hotelParent ? `${hotel?.hotelParent}/` : ""
    }${hotel?.slug?.current}`,
    description:
      hotel.seoGroup?.seoMetaDescription ||
      `Read a trusted review of ${hotel?.name}.`,
    image: hotel.reviewTextAndMedia?.homepageFeaturedImage?.asset
      ?.gatsbyImageData?.images?.fallback?.src
      ? `${hotel.reviewTextAndMedia.homepageFeaturedImage.asset.gatsbyImageData.images.fallback.src}`
      : undefined,
    address: {
      "@type": "PostalAddress",
      streetAddress: hotel?.addressGroup?.street,
      addressLocality:
        hotel?.addressGroup?.town || hotel?.addressGroup?.village,
      addressRegion: hotel?.addressGroup?.county,
      postalCode: hotel?.addressGroup?.postCode,
      addressCountry: hotel?.addressGroup?.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: hotel.location?.lat,
      longitude: hotel.location?.lng,
    },
    telephone: hotel.contactGroup?.telephone,
    priceRange: hotel.hotelInformation?.prices,
    petsAllowed: hotel.hotelInformation?.dogs?.includes("allowed") || false,
    paymentAccepted:
      hotel?.questionnaireTab?.q_CreditCards?.creditCardsAccepted?.join(),
    amenityFeature: hotel?.fromOldSiteToTransfer?.toTransferFacilities
      ?.split(", ")
      ?.map((facility) => ({
        "@type": "LocationFeatureSpecification",
        name: facility,
        value: true,
      })),
    sameAs: hotel.contactGroup?.websiteUrl || [],
    review: [
      {
        "@type": "Review",
        author: {
          "@type": "Organization",
          name: "Good Hotel Guide",
        },
        reviewBody:
          hotel.reviewTextAndMedia?.reviewContent?.[0]?.children?.[0]?.text ||
          "",
        publisher: {
          "@type": "Organization",
          name: "Good Hotel Guide",
          url: "https://www.goodhotelguide.com",
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title={
          hotel.seoGroup && hotel.seoGroup.seoMetaTitle
            ? hotel.seoGroup.seoMetaTitle
            : `${hotel.name} - Good Hotel Guide`
        }
        description={
          hotel.seoGroup &&
          hotel.seoGroup.seoMetaDescription &&
          hotel.seoGroup.seoMetaDescription
        }
        key={
          hotel.seoGroup && hotel.seoGroup.seoMetaTitle
            ? hotel.seoGroup.seoMetaTitle
            : `${hotel.name} - Good Hotel Guide`
        }
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </>
  );
};

// TODO: DOUBLE CHECK IS IT id OR _id???
export const query = graphql`
  query ($slug: String!, $hotelId: String!) {
    hotels: sanityHotel(slug: { current: { eq: $slug } }) {
      ...HotelFragment
    }
    # Get any special offers that have an id equal to hotel id - we add the hotel id in gatsby-node so we can access as a variable
    specialOffers: allSanitySpecialOffer(
      filter: { hotelId: { id: { eq: $hotelId } } }
    ) {
      nodes {
        ...SpecialOfferFragment
      }
    }
    inTheMedia: allSanityInTheMedia(
      filter: { hotelId: { elemMatch: { id: { eq: $hotelId } } } }
      sort: { publicationDate: DESC }
    ) {
      nodes {
        id
        _createdAt
        name
        publicationName
        externalLink
        hotelId {
          id
          name
        }
      }
    }
    # Retrieve any editors choice badges if the hotel features on an editor's choice page
    badges: allSanityPillar(
      filter: {
        editorsChoiceSection: {
          editorsChoiceSection: {
            elemMatch: {
              editorsSelectedHotels: { elemMatch: { id: { eq: $hotelId } } }
            }
          }
        }
        pillarPageOptions: { pageType: { eq: "editorsChoice" } }
      }
      sort: {
        editorsChoiceSection: {
          editorsChoiceSection: { awardYear: { value: ASC } }
        }
      }
    ) {
      nodes {
        slug {
          current
        }
        parentPage {
          slug {
            current
          }
        }
        editorsChoiceSection {
          editorsChoiceSection {
            title
            awardYear {
              value
            }
            editorsSelectedHotels {
              _id
            }
            editorsChoiceImage {
              imageItem {
                content {
                  asset {
                    gatsbyImageData(width: 100, placeholder: BLURRED)
                  }
                }
              }
            }
          }
        }
      }
    }
    placeholderImages: sanityPlaceholderImages {
      attractionPlaceholderImage: attractionsPlaceholder {
        asset {
          gatsbyImageData(width: 800, placeholder: BLURRED)
        }
      }
      generalPlaceholderImage: generalPlaceholder {
        asset {
          gatsbyImageData(width: 800, placeholder: BLURRED)
        }
      }
    }
    allHotelSearchInfo: allSanityHotel(
      filter: {
        _id: {
          nin: [
            "12edc423-f8c2-4a86-82f7-f757dad6a23c"
            "3a68f1ac-c963-441c-a96d-a5e506ee62ee"
          ]
        }
        displayOptions: { omitted: { eq: false }, extended: { eq: false } }
      }
    ) {
      nodes {
        _id
        _type
        name
        hotelParent: parentPage
        slug {
          current
        }
        displayOptions {
          paid
          inGuide
        }
        addressGroup {
          street
          town
          village
          postCode
          region
          county
          country
        }
        location {
          lat
          lng
        }
        reviewTextAndMedia {
          homepageFeaturedImage {
            asset {
              gatsbyImageData(width: 800, placeholder: BLURRED)
            }
          }
        }
      }
    }
    allPillarPagesSearchInfo: allSanityPillar {
      nodes {
        _id
        _type
        slug {
          current
        }
      }
    }
    siteSettings: sanitySiteSettings {
      aboutGoodHotelGuideExperts
      shortlistedHotelInfo
      inGuideNotPaidContent: _rawInGuideNotPaidContent(
        resolveReferences: { maxDepth: 5 }
      )
      hotelCtaText
    }
  }
`;
